/**
 * Tenant-Scoped Authentication Service
 * 
 * This is the main authentication service that combines provider-agnostic auth
 * with tenant-specific business logic. It uses dependency injection to work with
 * any auth provider (Supabase, Auth0, Firebase, etc.) and any database implementation.
 * 
 * Key Features:
 * - Provider-agnostic (easy to switch auth providers)
 * - Tenant-scoped authentication
 * - Automatic tenant validation
 * - Role-based access control
 * - Session management
 */

import type { IAuthProvider } from './interfaces/IAuthProvider';
import type { ITenantQueries } from './interfaces/ITenantQueries';
import type {
  TenantAuthResponse,
  TenantSignUpData,
  UserWithTenant,
  AuthSession,
  AuthStateChangeCallback,
  AuthSubscription,
} from './interfaces/types';
import {
  AuthError, TENANT_DEACTIVATED_CODE, TENANT_DEACTIVATED_MESSAGE, USER_DEACTIVATED_CODE, USER_DEACTIVATED_MESSAGE,
} from './interfaces/types';
import { SystemRole } from '@/config/permissions';
import { TenantStatus } from '@/services/dal/TenantService';

export class TenantAuthService {
  constructor(
    private authProvider: IAuthProvider,
    private tenantQueries: ITenantQueries
  ) {}

  /**
   * Sign in with tenant context
   * Validates that the user belongs to the specified tenant
   * 
   * @param email - User's email address
   * @param password - User's password
   * @param tenantSlug - Tenant identifier (e.g., 'credex-tech')
   * @returns User data with tenant information and session
   * @throws AuthError if authentication or tenant validation fails
   */
  async signInWithTenant(
    email: string,
    password: string,
    tenantSlug: string
  ): Promise<TenantAuthResponse> {
    // Step 1: Authenticate with the auth provider
    const authResult = await this.authProvider.signIn(email, password);

    if (!authResult.user || !authResult.session) {
      throw new AuthError('Authentication failed - no user or session returned');
    }

    // Step 2: Validate user belongs to the specified tenant
    const userProfile = await this.tenantQueries.getUserByEmailAndTenantSlug(
      email,
      tenantSlug
    );

    if (!userProfile) {
      // Sign out the user since tenant validation failed
      // Important: We do this silently to avoid SIGNED_OUT event interfering with login flow
      await this.authProvider.signOut();
      throw new AuthError(
        `User does not belong to tenant '${tenantSlug}' or account is inactive`,
        'TENANT_VALIDATION_FAILED',
        403
      );
    }

    await this.assertTenantActive(userProfile);

    // Step 3: Return structured user data with tenant information
    return {
      user: userProfile,
      session: authResult.session,
      authUser: authResult.user,
    };
  }

  /**
   * Sign up a new user with tenant assignment
   * Creates both auth user and profile with tenant association
   * 
   * @param signUpData - User registration data including tenant_id
   * @returns User data with tenant information and session
   * @throws AuthError if sign up or profile creation fails
   */
  async signUpWithTenant(signUpData: TenantSignUpData): Promise<TenantAuthResponse> {
    // Step 1: Create auth user
    const authResult = await this.authProvider.signUp(
      signUpData.email,
      signUpData.password,
      { name: signUpData.name }
    );

    if (!authResult.user || !authResult.session) {
      throw new AuthError('Sign up failed - no user or session returned');
    }

    // Note: Profile creation should be handled by a database trigger or
    // a separate profile service. This is just fetching the created profile.
    
    // Step 2: Fetch complete profile with tenant info
    const userProfile = await this.tenantQueries.getUserProfileWithTenant(
      authResult.user.id
    );

    if (!userProfile) {
      throw new AuthError('Failed to fetch user profile after creation');
    }

    return {
      user: userProfile,
      session: authResult.session,
      authUser: authResult.user,
    };
  }

  /**
   * Get current user with tenant information
   * Fetches complete profile including tenant and roles
   * 
   * @returns User data with tenant information or null if not authenticated
   */
  async getCurrentUserWithTenant(): Promise<UserWithTenant | null> {
    const user = await this.authProvider.getUser();

    if (!user) {
      return null;
    }

    const profile = await this.tenantQueries.getUserProfileWithTenant(user.id);
    if (profile) {
      await this.assertTenantActive(profile);
    }
    return profile;
  }

  /**
   * A deactivated client's users are signed out and refused. The super-admin is exempt so they
   * can still reach Tenant Configuration and reactivate. The engine enforces the same rule.
   */
  private async assertTenantActive(user: UserWithTenant): Promise<void> {
    // A deactivated user (User Management → Deactivate) is refused the same way ([UAT-U5]); no role
    // is exempt — another admin reactivates them. The engine enforces the same rule.
    if (user.is_active === false) {
      await this.authProvider.signOut();
      throw new AuthError(USER_DEACTIVATED_MESSAGE, USER_DEACTIVATED_CODE, 403);
    }
    const status = user.tenant.status;
    if (!status || status === TenantStatus.ACTIVE || user.roles.includes(SystemRole.SUPER_ADMIN)) {
      return;
    }
    await this.authProvider.signOut();
    throw new AuthError(TENANT_DEACTIVATED_MESSAGE, TENANT_DEACTIVATED_CODE, 403);
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    console.log('[TenantAuthService] signOut called');
    await this.authProvider.signOut();
    console.log('[TenantAuthService] authProvider.signOut completed');
  }

  /**
   * Get current session
   */
  async getSession(): Promise<AuthSession | null> {
    return this.authProvider.getSession();
  }

  /**
   * Refresh the current session
   */
  async refreshSession(): Promise<AuthSession | null> {
    return this.authProvider.refreshSession();
  }

  /**
   * Listen to authentication state changes
   */
  onAuthStateChange(callback: AuthStateChangeCallback): AuthSubscription {
    return this.authProvider.onAuthStateChange(callback);
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<void> {
    await this.authProvider.resetPassword(email);
  }

  /**
   * Update user password
   */
  async updatePassword(newPassword: string): Promise<void> {
    await this.authProvider.updatePassword(newPassword);
  }
}

